/**
 * Deterministic mock data generators for L2 (services) and L3 (stacking).
 * Uses a tail-number hash so the same aircraft always gets the same mock data
 * across renders and view modes.
 */

import type { ScheduleItem } from './useTodayBoardData'
import type { MockServiceTask, MockStackingDetail, ServiceStatus, ServiceType } from './types'

// ── Hash helper ─────────────────────────────────────

function hashTail(tail: string): number {
  let h = 0
  for (let i = 0; i < tail.length; i++) {
    h = ((h << 5) - h + tail.charCodeAt(i)) | 0
  }
  return Math.abs(h)
}

// ── Service name templates ──────────────────────────

const SERVICE_TEMPLATES: { type: ServiceType; name: string; minMinutes: number; maxMinutes: number }[] = [
  { type: 'tow', name: 'Tow to staging area', minMinutes: 5, maxMinutes: 10 },
  { type: 'fuel', name: 'Fuel 800 gal Jet-A', minMinutes: 15, maxMinutes: 35 },
  { type: 'fuel', name: 'Fuel 400 gal Jet-A', minMinutes: 10, maxMinutes: 20 },
  { type: 'deice', name: 'Deice Type I/IV', minMinutes: 10, maxMinutes: 25 },
  { type: 'lav', name: 'Lav service + grey water', minMinutes: 5, maxMinutes: 10 },
  { type: 'catering', name: 'Catering (Landmark Provisions)', minMinutes: 5, maxMinutes: 15 },
  { type: 'gpu', name: 'GPU hookup + cabin pre-cool', minMinutes: 5, maxMinutes: 15 },
  { type: 'tow', name: 'Tow to hangar HC-2', minMinutes: 5, maxMinutes: 15 },
  { type: 'customs', name: 'CBP clearance hold', minMinutes: 10, maxMinutes: 30 },
  { type: 'deice', name: 'Anti-ice Type IV holdover', minMinutes: 5, maxMinutes: 15 },
  { type: 'catering', name: 'Catering drop-off (pax 6)', minMinutes: 5, maxMinutes: 10 },
  { type: 'gpu', name: 'Ground power standby', minMinutes: 10, maxMinutes: 20 },
]

// ── Fuel quantity helper ────────────────────────────

export function getMockFuelQuantity(tailNumber: string): string {
  const h = hashTail(tailNumber)
  const gallons = [200, 400, 500, 600, 800, 1000, 1200, 1500]
  return `${gallons[h % gallons.length]} gal`
}

// ── Deterministic status from hash ──────────────────

function statusFromHash(h: number, idx: number): ServiceStatus {
  const v = (h + idx * 17) % 10
  if (v < 3) return 'completed'
  if (v < 5) return 'in_progress'
  return 'pending'
}

// ── Public API ──────────────────────────────────────

export function getMockServicesForMovement(item: ScheduleItem): MockServiceTask[] {
  const tail = item.data.aircraft.tailNumber
  const h = hashTail(tail)
  const count = 2 + (h % 5) // 2-6 services

  const services: MockServiceTask[] = []
  for (let i = 0; i < count; i++) {
    const tpl = SERVICE_TEMPLATES[(h + i * 7) % SERVICE_TEMPLATES.length]
    const minutes = tpl.minMinutes + ((h + i * 13) % (tpl.maxMinutes - tpl.minMinutes + 1))
    services.push({
      id: `mock-svc-${tail}-${i}`,
      type: tpl.type,
      name: tpl.name,
      estimatedMinutes: minutes,
      status: statusFromHash(h, i),
    })
  }

  return services
}

export function getMockStackingFlag(item: ScheduleItem): boolean {
  // ~25% of items flagged for stacking
  return hashTail(item.data.aircraft.tailNumber) % 4 === 0
}

// ── Stacking fleet names (deterministic) ────────────

const FLEET_TAILS = [
  'N123AV', 'N456GX', 'N789PC', 'N321LX', 'N654WB',
  'N987TG', 'N210KR', 'N555JM', 'N888QR', 'N112FH',
]

export function getMockStackingDetail(item: ScheduleItem): MockStackingDetail | null {
  if (!getMockStackingFlag(item)) return null

  const tail = item.data.aircraft.tailNumber
  const h = hashTail(tail)

  const blockerTail = FLEET_TAILS[h % FLEET_TAILS.length]
  const shiftTail = FLEET_TAILS[(h + 3) % FLEET_TAILS.length]

  return {
    trigger: `${tail} arrival requires hangar shuffle`,
    totalMinutes: 15 + (h % 20),
    crewRequired: '2 tug operators + 1 wing walker',
    notes: [
      `${blockerTail} departs at 14:45 — timing-dependent`,
      `Wingspan clearance: ${4 + (h % 3)}ft — wing walker required`,
    ],
    steps: [
      { id: `st-${tail}-1`, action: 'Pull Out', aircraft: blockerTail, from: 'HC-1', to: 'Ramp Hold', duration: 5, status: 'pending' },
      { id: `st-${tail}-2`, action: 'Shift', aircraft: shiftTail, from: 'HC-3', to: 'HC-2', duration: 10, status: 'pending' },
      { id: `st-${tail}-3`, action: 'Tow In', aircraft: tail, from: 'Staging', to: 'HC-1', duration: 5, status: 'pending' },
    ],
  }
}

export function getMockDurationMinutes(item: ScheduleItem): number {
  const services = getMockServicesForMovement(item)
  return services.reduce((sum, s) => sum + s.estimatedMinutes, 0)
}

export function getMockProgress(item: ScheduleItem): { completed: number; total: number; inProgress: number } {
  const services = getMockServicesForMovement(item)
  return {
    completed: services.filter((s) => s.status === 'completed').length,
    inProgress: services.filter((s) => s.status === 'in_progress').length,
    total: services.length,
  }
}
